import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import { VISIBILITY, WIDGET_TYPES } from '../lib/WidgetConstants';

export default function EditWidgetModal({ widget, onClose, onSave }) {
  const [formData, setFormData] = useState(null);

  useEffect(() => {
    if (widget) {
      setFormData(JSON.parse(JSON.stringify(widget)));
    }
  }, [widget]);

  if (!widget || !formData) return null;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSettingsChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      settings: { ...prev.settings, [field]: value }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const widgetTypeLabel = WIDGET_TYPES.find(w => w.value === widget.type)?.label || widget.type;

  return (
    <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-[#0d111a] border border-white/10 rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95">
        <div className="flex items-center justify-between p-4 border-b border-white/10 bg-black/20">
          <h3 className="font-bold text-white flex items-center gap-2">
            Edit Block: <span className="text-[var(--tf-primary)]">{widgetTypeLabel}</span>
          </h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-xs font-bold text-zinc-400 uppercase mb-1">Title</label>
            <input 
              type="text" 
              value={formData.title || ""} 
              onChange={e => handleChange("title", e.target.value)}
              placeholder={`Default: ${widgetTypeLabel}`}
              className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white focus:border-[var(--tf-primary)] outline-none"
            />
          </div>
          
          <div>
            <label className="block text-xs font-bold text-zinc-400 uppercase mb-1">Visibility</label>
            <select 
              value={formData.settings?.visibility || "all"} 
              onChange={e => handleSettingsChange("visibility", e.target.value)}
              className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white focus:border-[var(--tf-primary)] outline-none"
            >
              {VISIBILITY.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
            </select>
          </div>
          
          {['recent_posts', 'top_users', 'online_users', 'categories'].includes(formData.type) && (
            <div>
              <label className="block text-xs font-bold text-zinc-400 uppercase mb-1">Items Count</label>
              <input 
                type="number" 
                min="1" max="50"
                value={formData.settings?.items_count || 5} 
                onChange={e => handleSettingsChange("items_count", parseInt(e.target.value))}
                className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white focus:border-[var(--tf-primary)] outline-none"
              />
            </div>
          )}

          {formData.type === "html" && (
            <div>
              <label className="block text-xs font-bold text-zinc-400 uppercase mb-1">Custom HTML</label>
              <textarea 
                rows={4}
                value={formData.settings?.custom_html || ""} 
                onChange={e => handleSettingsChange("custom_html", e.target.value)}
                className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white focus:border-[var(--tf-primary)] outline-none font-mono"
              />
            </div>
          )}

          <div className="pt-4 flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded text-sm text-zinc-400 hover:text-white transition-colors">Cancel</button>
            <button type="submit" className="px-4 py-2 rounded text-sm bg-[var(--tf-primary)] hover:bg-[var(--tf-primary-hover)] text-white font-bold flex items-center gap-2 transition-colors">
              <Save className="w-4 h-4" /> Save Settings
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
